import { useEffect, useState } from 'react';
import {
  generatePath, useNavigate, useSearchParams,
} from 'react-router-dom';
import AppRoutes from 'constants/AppRoutes';
import { SEARCH_PARAMS } from 'constants/dataSets';
import {
  SHOW_ARCHIVED,
} from 'constants/formFields';
import {
  USER_PILLAR_MODULES,
} from 'constants/interfaces';
import useDataSetListActions from 'hooks/useDataSetListActions';
import useDownloadExample from 'hooks/useDownloadExample';
import useImportErrorModal from 'hooks/useImportErrorModal';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import {
  useLazyProfitCenterSheetExampleQuery, useProfitCenterArchiveMutation,
  useProfitCenterDeArchiveMutation,
  useProfitCenterImportRecordsMutation,
  useProfitCenterListQuery,
  useProfitCenterRecordDeleteMutation,
} from 'store/slices/dataSets/apis/dataSetsApi';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';

import useDataSetsListForm from './hooks/useDataSetsListForm';
import ProfitCentresView from './ProfitCentresView';

import type IProfitCenterItem from 'store/slices/dataSets/interfaces/IProfitCenterItem';
import type IArchiveError from './interfaces/IArchiveError';
import type { TUseProfitCenterImportRecordsMutation } from './interfaces/TUseProfitCenterImportRecordsMutation';

function ProfitCentres() {
  const {
    isActionsVisible,
    isLoading: isPillarModuleLoading,
  } = usePillarModulePermission(
    { pillarModuleKey: USER_PILLAR_MODULES.settings.dataSetsManagement },
  );
  const navigate = useNavigate();
  const {
    control, getValues, setValue, watch,
  } = useDataSetsListForm();
  const [orderBy, setOrderBy] = useState('');
  const [searchParams] = useSearchParams();
  const [hasTriedArchived, setHasTriedArchived] = useState(false);

  const {
    isImportErrorModalVisible,
    importErrorMessage,
    handleImportError,
    handleCloseImportErrorModal,
  } = useImportErrorModal();

  const isArchived = watch(SHOW_ARCHIVED);
  const searchParam = searchParams.get(SEARCH_PARAMS.search) || '';
  const offset = Number(searchParams.get(SEARCH_PARAMS.offset) || 0);

  useEffect(() => {
    const isArchivedParam = searchParams.get(SEARCH_PARAMS.isArchived);
    const isArchivedFromUrl = isArchivedParam !== null ? Boolean(isArchivedParam === 'true') : false;

    if (isArchivedFromUrl !== isArchived) {
      setValue(SHOW_ARCHIVED, isArchivedFromUrl);
    }
  }, [searchParams, isArchived, setValue]);

  const { data, isFetching: isProfitCenterListLoading } = useProfitCenterListQuery({
    orderBy, keyword: searchParam, archived: isArchived, offset,
  });
  const [getProfitCenterSheetExample, {
    isLoading: isLazyProfitCenterSheetLoading,
  }] = useLazyProfitCenterSheetExampleQuery();

  const [deArchive,
    { isLoading: isProfitCenterDeArchiveLoading, error: deArchiveError }] = useProfitCenterDeArchiveMutation();

  const [archive, { isLoading: isProfitCenterArchiveLoading, error: archiveError }] = useProfitCenterArchiveMutation();

  const [deleteRecord,
    {
      isLoading: isProfitCenterDeleteLoading,
      error: deleteError,
    }] = useProfitCenterRecordDeleteMutation();

  const [importRecords, { isLoading: isProfitCenterImportRecordsLoading }] = useProfitCenterImportRecordsMutation();

  const { data: locationsCountries, isFetching: isLocationsCountriesLoading } = useGetLocationsCountriesQuery();

  const { meta, items } = data?.data || {};

  const {
    isArchiveModalVisible,
    isArchiveModalInfoVisible,
    latestArchivation,
    isLineMark,
    onSortChange,
    onSearchChange,
    onSelectAll,
    onArchiveConfirm,
    onArchive,
    onArchiveModalInfoOpen,
    onMoveToArchive,
    onDeArchive,
    onPageChange,
    isArchiveSelectedItemsVisible,
    setIsArchiveModalVisible,
    setIsArchiveModalInfoVisible,
    selectedItems,
    onShowArchiveChange,
    onOpenRestrictedModal,
    activeRestrictedModal,
    onDeleteRecord,
    isDeleteModalVisible,
    onCloseDeleteModal,
    onDeleteConfirm,
  } = useDataSetListActions<IProfitCenterItem[]>({
    items,
    archiveAction: archive,
    deArchiveAction: deArchive,
    deleteAction: deleteRecord,
    setOrderBy,
    getValues,
    setValue,
    watch,
  });

  const isWithSearch = !!searchParam;

  const isEmptyList = !data?.data?.meta?.count || data?.data?.items?.length === 0;

  const currentTotalItems = items?.length || 0;

  const isLoading = isProfitCenterListLoading || isProfitCenterImportRecordsLoading || isProfitCenterDeArchiveLoading
        || isProfitCenterArchiveLoading || isLazyProfitCenterSheetLoading
        || isPillarModuleLoading || isLocationsCountriesLoading || isProfitCenterDeleteLoading;

  useEffect(() => {
    const isArchivedParam = searchParams.get(SEARCH_PARAMS.isArchived);
    if (isEmptyList && !isLoading && !isArchivedParam && !hasTriedArchived) {
      setHasTriedArchived(true);
      onShowArchiveChange(true);
    }
  }, [isEmptyList, isLoading, searchParams, onShowArchiveChange, hasTriedArchived]);

  const {
    uploadControl, isMappingModalVisible, onCloseMappingModal, isImportErrorsModalVisible,
    setIsImportErrorsModalVisible,
  } = useDownloadExample<TUseProfitCenterImportRecordsMutation>({
    importRecords,
    onError: handleImportError,
  });

  const onDownloadExample = () => {
    getProfitCenterSheetExample();
  };

  const onEditRecord = (recordId: number) => {
    navigate(generatePath(AppRoutes.profitCentresEdit, { recordId: String(recordId) }));
  };

  const onAddRecord = () => {
    navigate(AppRoutes.profitCentresCreate);
  };

  return (
    <ProfitCentresView
      isEmptyList={isEmptyList}
      uploadControl={uploadControl}
      isActionsVisible={isActionsVisible}
      onDownloadExample={onDownloadExample}
      isMappingModalVisible={isMappingModalVisible}
      onCloseMappingModal={onCloseMappingModal}
      isImportErrorsModalVisible={isImportErrorsModalVisible}
      setIsImportErrorsModalVisible={setIsImportErrorsModalVisible}
      control={control}
      meta={meta}
      items={items}
      onSortChange={onSortChange}
      onSearchChange={onSearchChange}
      onSelectAll={onSelectAll}
      isLineMark={isLineMark}
      onArchive={onArchive}
      setIsArchiveModalVisible={setIsArchiveModalVisible}
      onArchiveConfirm={onArchiveConfirm}
      isArchiveModalVisible={isArchiveModalVisible}
      isArchiveSelectedItemsVisible={isArchiveSelectedItemsVisible}
      isArchiveModalInfoVisible={isArchiveModalInfoVisible}
      setIsArchiveModalInfoVisible={setIsArchiveModalInfoVisible}
      latestArchivation={latestArchivation}
      onArchiveModalInfoOpen={onArchiveModalInfoOpen}
      onMoveToArchive={onMoveToArchive}
      onDeArchive={onDeArchive}
      selectedItems={selectedItems}
      onPageChange={onPageChange}
      currentTotalItems={currentTotalItems}
      onEditRecord={onEditRecord}
      importRecords={importRecords}
      onAddRecord={onAddRecord}
      isWithSearch={isWithSearch}
      isLoading={isLoading}
      isLazyProfitCenterSheetLoading={isLazyProfitCenterSheetLoading}
      onShowArchiveChange={onShowArchiveChange}
      activeModal={activeRestrictedModal}
      closeModal={onOpenRestrictedModal}
      archiveError={archiveError as IArchiveError || deArchiveError as IArchiveError || deleteError as IArchiveError}
      locationsCountries={locationsCountries?.data || []}
      onDeleteRecord={onDeleteRecord}
      isDeleteModalVisible={isDeleteModalVisible}
      onCloseDeleteModal={onCloseDeleteModal}
      onDeleteConfirm={onDeleteConfirm}
      isImportErrorModalVisible={isImportErrorModalVisible}
      importErrorMessage={importErrorMessage}
      onCloseImportErrorModal={handleCloseImportErrorModal}
      onImportError={handleImportError}
    />
  );
}

export default ProfitCentres;
